import React from 'react'
import { css, styled } from 'styled-components'

import type { SearchProperty } from '@/api/models/SearchIncludedResponse'
import { formatPrice } from '@/lib/helpers'
import { theme } from '@/theme'

import type { TotalPrice } from '../../../../api/types/hor'

type PropertyPriceSummaryProps = {
  totalPrice: TotalPrice
  currencySymbol: string
  priceOnHold?: SearchProperty['priceOnHold']
  unitOnHold?: SearchProperty['unitOnHold']
}

const capsuleStyles = css`
  display: flex;
  flex-direction: column;
  border-radius: 16px;
`

const dividerStyles = css`
  padding-bottom: 8px;
  border-bottom: 1px dashed ${theme.colors.borderColor};
`

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  font-family: ${theme.fonts.itcGothicPro};
  font-weight: ${theme.fontWeight.semiBold};
  font-size: 18px;
  line-height: 22px;
  padding-bottom: 4px;
`

const Wrapper = styled.div`
  gap: 8px;
  ${capsuleStyles}
  align-items: center;

  padding: 20px;

  background: ${theme.colors.aliceBlue};
`

const Subsection = styled.div`
  ${capsuleStyles}
  width: 100%;
  justify-content: center;

  padding: 16px;
  gap: 8px;

  background: ${theme.colors.white};
`

const SubsectionTitle = styled.div`
  font-size: 14px;
  font-weight: ${theme.fontWeight.semiBold};
  ${dividerStyles}
`

const TotalPriceTitle = styled.div`
  font-family: ${theme.fonts.itcGothicPro};
  font-weight: ${theme.fontWeight.semiBold};
`

const TotalPriceValue = styled.div`
  font-weight: ${theme.fontWeight.medium};
`

const TotalPriceWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  font-size: 18px;
  justify-content: space-between;
  align-items: baseline;
`

const TotalPriceLine = ({
  price,
  currencySymbol,
}: {
  price: number
  currencySymbol: string
}) => (
  <TotalPriceWrapper>
    <TotalPriceTitle>Total Price</TotalPriceTitle>
    <TotalPriceValue>{formatPrice(price, currencySymbol)}</TotalPriceValue>
  </TotalPriceWrapper>
)

const FeeLineWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  font-weight: ${theme.fontWeight.medium};

  &:not(:last-child) {
    ${dividerStyles}
  }
`

const FeeLineDescription = styled.div`
  display: flex;
  flex-direction: column;
`

const FeeLineTitle = styled.div`
  font-size: 14px;
`

const FeeLineSubtitle = styled.div`
  font-weight: ${theme.fontWeight.light};
  font-size: 12px;
  color: ${theme.colors.greyText2};
`

const FeeLinePrice = styled.div`
  font-size: 16px;
  text-align: right;
`

const FeeLine = ({
  title,
  subtitle,
  price,
  currencySymbol,
}: {
  title: string
  subtitle?: string
  price: number
  currencySymbol: string
}) => (
  <FeeLineWrapper>
    <FeeLineDescription>
      <FeeLineTitle>{title}</FeeLineTitle>
      {subtitle && <FeeLineSubtitle>{subtitle}</FeeLineSubtitle>}
    </FeeLineDescription>
    <FeeLinePrice>{formatPrice(price, currencySymbol)}</FeeLinePrice>
  </FeeLineWrapper>
)

const HoldLineWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: baseline;
  column-gap: 4px;
  font-weight: ${theme.fontWeight.light};
`

const HoldLineName = styled.div`
  font-weight: ${theme.fontWeight.medium};
`

const HoldLineDivider = styled.div`
  flex-grow: 1;
  flex-shrink: 1;
  ${dividerStyles}
`

const HoldLine = ({ name, value }: { name: string; value: number }) => (
  <HoldLineWrapper>
    <HoldLineName>{name}</HoldLineName>
    <HoldLineDivider />
    <div>{value} hours</div>
  </HoldLineWrapper>
)

export const PriceSummary = ({
  totalPrice,
  currencySymbol,
  priceOnHold,
  unitOnHold,
}: PropertyPriceSummaryProps) => {
  const {
    typeNightly: isNightly,
    nightCount,
    pricePerNight,
    total,
    feeApplyValueList,
    feeApplyTotal,
  } = totalPrice
  const dayOrNight = isNightly ? 'Night' : 'Day'
  const hasHoldData = priceOnHold || unitOnHold

  const hasExtraFees = feeApplyValueList.length > 0

  return (
    <Wrapper>
      <SectionTitle>
        <div>Price Summary</div>
      </SectionTitle>
      <Subsection>
        <FeeLine
          title={`${nightCount} ${dayOrNight} Stay`}
          subtitle={`Price per ${dayOrNight}`}
          price={pricePerNight}
          currencySymbol={currencySymbol}
        />
      </Subsection>
      {hasExtraFees && (
        <Subsection>
          <SubsectionTitle>Additional Fees</SubsectionTitle>
          {feeApplyValueList.map(({ title, value, discriminator, cost }) => {
            const isPercentage = discriminator.percentage
            const price = isPercentage ? cost : value
            const subtitle = isPercentage
              ? `Stay percentage ${value}%`
              : discriminator.title
            return (
              <FeeLine
                key={title}
                title={title}
                subtitle={subtitle}
                price={price}
                currencySymbol={currencySymbol}
              />
            )
          })}
          <FeeLine
            title="Fees Total"
            price={feeApplyTotal}
            currencySymbol={currencySymbol}
          />
        </Subsection>
      )}
      <Subsection>
        <TotalPriceLine price={total} currencySymbol={currencySymbol} />
      </Subsection>
      {hasHoldData && (
        <Subsection>
          <HoldLine name="Price On Hold" value={priceOnHold} />
          <HoldLine name="Unit On Hold" value={unitOnHold} />
        </Subsection>
      )}
    </Wrapper>
  )
}
